import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    IOpcion,
    NODO_TIPO,
    NODO_TIPO_ICONO,
    NODO_TIPO_LABEL,
    NodoTipo,
} from 'src/app/models/notificacion/whatsapp_flujo.model';

/**
 * Tarjeta (`p-card`) que resume un paso dentro de `flujo-nodo-lista` (S4.4).
 * Puramente de presentación: no toca el `FormGroup`, solo lo lee para
 * mostrar un resumen — la edición real ocurre en `flujo-nodo-editor`
 * (`p-sidebar`), que sí trabaja directo sobre el mismo `FormGroup`.
 *
 * No muestra el id interno del nodo ni el nombre del tipo en jerga: quien usa
 * esta pantalla es el usuario final, no el equipo técnico.
 */
@Component({
    selector: 'app-flujo-nodo',
    templateUrl: './flujo-nodo.component.html',
})
export class FlujoNodoComponent {

    @Input() nodoForm!: FormGroup;
    @Input() esInicial = false;
    @Input() numero = 0;
    /** Etiqueta del botón que lleva a este paso, si alguno lo hace. */
    @Input() disparador = '';
    /** Ningún camino desde este paso llega a un END/HANDOFF (ciclo cerrado sin salida). Viene calculado del padre: requiere ver el flujo entero, no solo este nodo. */
    @Input() sinFinal = false;
    /** Ningún camino desde el primer paso llega hasta este. Viene calculado del padre: requiere ver el flujo entero, no solo este nodo. */
    @Input() inalcanzable = false;

    @Output() editar = new EventEmitter<void>();
    @Output() eliminar = new EventEmitter<void>();
    @Output() marcarComoInicial = new EventEmitter<void>();

    get tipo(): NodoTipo {
        return this.nodoForm.get('tipo')?.value;
    }

    get etiquetaTipo(): string {
        return NODO_TIPO_LABEL[this.tipo] || 'Sin definir';
    }

    get icono(): string {
        return NODO_TIPO_ICONO[this.tipo] || 'pi pi-question';
    }

    /** El paso se nombra por su causa, no por su número: es lo que quien lo escribió recuerda. */
    get titulo(): string {
        if (this.esInicial) {
            return 'Primer paso';
        }

        return this.disparador ? `Cuando toquen «${this.disparador}»` : `Paso ${this.numero}`;
    }

    get subtitulo(): string {
        return this.esInicial ? 'Los botones de la plantilla' : this.etiquetaTipo;
    }

    get texto(): string {
        return String(this.nodoForm.get('texto')?.value || '').trim();
    }

    get textoPreview(): string {
        return this.texto.length > 80 ? `${this.texto.slice(0, 80)}…` : this.texto;
    }

    get opciones(): IOpcion[] {
        return (this.nodoForm.get('opciones')?.value || []) as IOpcion[];
    }

    /** Lo que le falta a este paso, en lenguaje de usuario. Reemplaza al "Faltan datos obligatorios" genérico. */
    get faltantes(): string[] {
        const faltan: string[] = [];

        if (!this.texto) {
            faltan.push(this.esInicial
                ? 'Falta qué contesta el bot si el cliente escribe en vez de tocar un botón'
                : 'Falta el mensaje que le manda al cliente');
        }

        const sinDestino = this.opciones.filter((opcion) => !String(opcion?.destinoNodoId || '').trim());
        if (sinDestino.length) {
            const etiquetas = sinDestino.map((opcion) => `"${opcion.etiqueta || opcion.id}"`).join(', ');
            faltan.push(`Falta elegir a dónde lleva ${etiquetas}`);
        }

        if (this.tipo === NODO_TIPO.MESSAGE && !String(this.nodoForm.get('siguienteNodoId')?.value || '').trim()) {
            faltan.push('Falta elegir con qué paso sigue después de este mensaje');
        }

        if (this.tipo === NODO_TIPO.CHOICE && !this.opciones.length) {
            faltan.push('Falta agregar al menos un botón');
        }

        if (this.sinFinal) {
            faltan.push('Desde este paso no hay forma de terminar la conversación');
        }

        if (this.inalcanzable) {
            faltan.push('Ningún botón ni paso anterior lleva hasta este paso: nunca se va a mostrar.');
        }

        return faltan;
    }

    get completo(): boolean {
        return this.faltantes.length === 0;
    }
}
